import React from 'react'
import {
  Edit, Eye, Library, Sparkles, Send, ThumbsUp, ThumbsDown,
  X, MoreVertical, Trash2
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ObjectiveTemplate } from '@/services/api/templateService'
import { STATUS_CONFIGS, TEMPLATE_TYPE_CONFIGS, ViewMode } from '@/utils/templateImportUtils'

interface TemplatesTabContentProps {
  viewMode: ViewMode
  loading: boolean
  templates: ObjectiveTemplate[]
  filteredTemplates: ObjectiveTemplate[]
  handlePreviewTemplate: (template: ObjectiveTemplate) => void
  handleEditTemplate: (template: ObjectiveTemplate) => void
  handleDeleteTemplate: (templateId: number) => void
  handlePublishToggle: (template: ObjectiveTemplate) => void
  handleSubmitForApproval: (templateId: number) => void
  handlePublishDraft: (templateId: number) => void
  handleApproveClick: (template: ObjectiveTemplate) => void
  handleRejectClick: (template: ObjectiveTemplate) => void
  clearFilters: () => void
}

const TemplatesTabContent: React.FC<TemplatesTabContentProps> = ({
  viewMode,
  loading,
  templates,
  filteredTemplates,
  handlePreviewTemplate,
  handleEditTemplate,
  handleDeleteTemplate,
  handlePublishToggle,
  handleSubmitForApproval,
  handlePublishDraft,
  handleApproveClick,
  handleRejectClick,
  clearFilters,
}) => {
  const renderTemplateDropdown = (template: ObjectiveTemplate) => (
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={() => handlePreviewTemplate(template)}>
        <Eye className="h-4 w-4 mr-2" />
        Preview
      </DropdownMenuItem>
      {(template.status === 'draft' || template.status === 'rejected') && (
        <>
          <DropdownMenuItem onClick={() => handleEditTemplate(template)}>
            <Edit className="h-4 w-4 mr-2 text-blue-600" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleSubmitForApproval(template.id)}>
            <Send className="h-4 w-4 mr-2 text-orange-600" />
            Submit for Approval
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePublishDraft(template.id)}>
            <Sparkles className="h-4 w-4 mr-2 text-green-600" />
            Publish Directly
          </DropdownMenuItem>
        </>
      )}
      {template.status === 'pending_approval' && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleApproveClick(template)}>
            <ThumbsUp className="h-4 w-4 mr-2 text-green-600" />
            Approve
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleRejectClick(template)}>
            <ThumbsDown className="h-4 w-4 mr-2 text-red-600" />
            Reject
          </DropdownMenuItem>
        </>
      )}
      {template.status === 'approved' && (
        <>
          <DropdownMenuSeparator />
          {!template.is_published && (
            <DropdownMenuItem onClick={() => handleEditTemplate(template)}>
              <Edit className="h-4 w-4 mr-2 text-blue-600" />
              Edit
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => handlePublishToggle(template)}>
            {template.is_published ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Unpublish
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Publish
              </>
            )}
          </DropdownMenuItem>
        </>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={() => handleDeleteTemplate(template.id)}
        className="text-red-600"
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  )

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Template Library</h3>
          <p className="text-sm text-slate-600 mt-1">
            Showing {filteredTemplates.length} of {templates.length} templates
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 bg-slate-50 rounded-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-600 mt-4 font-medium">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-lg">
          <Library className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {templates.length === 0 ? 'No templates yet' : 'No matching templates'}
          </h3>
          <p className="text-slate-600 mb-6">
            {templates.length === 0
              ? 'Templates will appear here once managers create them'
              : 'Try adjusting your filters or search query'
            }
          </p>
          {templates.length > 0 && (
            <Button onClick={clearFilters} variant="outline">
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => {
                const statusConfig = STATUS_CONFIGS[template.status] || STATUS_CONFIGS['draft']
                const typeConfig = TEMPLATE_TYPE_CONFIGS[template.template_type] || TEMPLATE_TYPE_CONFIGS['generic']
                const objectiveCount = template.objectives_data?.objectives?.length || 0

                return (
                  <Card key={template.id} className="hover:shadow-lg transition-all hover:border-blue-200 group">
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{template.name}</h3>
                            <p className="text-xs text-slate-600 line-clamp-2 mt-1">{template.description}</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            {renderTemplateDropdown(template)}
                          </DropdownMenu>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2">
                          <Badge className={`${statusConfig.bgColor} ${statusConfig.color} flex items-center gap-1`}>
                            {statusConfig.icon}
                            {statusConfig.label}
                          </Badge>
                          <Badge className={`${typeConfig.bgColor} ${typeConfig.color}`}>
                            {template.template_type.replace(/_/g, ' ')}
                          </Badge>
                          {template.is_published && (
                            <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                              <Sparkles className="h-3 w-3" />
                              Published
                            </Badge>
                          )}
                        </div>

                        {/* Metrics */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-slate-50 rounded p-2">
                            <div className="text-slate-600">Objectives</div>
                            <div className="font-semibold text-slate-900">{objectiveCount}</div>
                          </div>
                          <div className="bg-slate-50 rounded p-2">
                            <div className="text-slate-600">Used</div>
                            <div className="font-semibold text-slate-900">{template.usage_count} times</div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="text-xs text-slate-500 border-t pt-2">
                          Created: {new Date(template.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="space-y-3">
              {filteredTemplates.map((template) => {
                const statusConfig = STATUS_CONFIGS[template.status] || STATUS_CONFIGS['draft']
                const typeConfig = TEMPLATE_TYPE_CONFIGS[template.template_type] || TEMPLATE_TYPE_CONFIGS['generic']
                const objectiveCount = template.objectives_data?.objectives?.length || 0

                return (
                  <Card key={template.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-900">{template.name}</h4>
                              <p className="text-sm text-slate-600 mt-1 line-clamp-1">{template.description}</p>

                              <div className="flex flex-wrap gap-2 mt-3">
                                <Badge className={`${statusConfig.bgColor} ${statusConfig.color} flex items-center gap-1 text-xs`}>
                                  {statusConfig.icon}
                                  {statusConfig.label}
                                </Badge>
                                <Badge className={`${typeConfig.bgColor} ${typeConfig.color} text-xs`}>
                                  {template.template_type.replace(/_/g, ' ')}
                                </Badge>
                                {template.is_published && (
                                  <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1 text-xs">
                                    <Sparkles className="h-3 w-3" />
                                    Published
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {objectiveCount} objectives
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {template.usage_count} uses
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          {renderTemplateDropdown(template)}
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}

          {/* Compact View */}
          {viewMode === 'compact' && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase">
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1">Objectives</div>
                <div className="col-span-1">Uses</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-1">Actions</div>
              </div>
              <div className="divide-y divide-slate-200">
                {filteredTemplates.map((template) => {
                  const statusConfig = STATUS_CONFIGS[template.status] || STATUS_CONFIGS['draft']
                  const typeConfig = TEMPLATE_TYPE_CONFIGS[template.template_type] || TEMPLATE_TYPE_CONFIGS['generic']
                  const objectiveCount = template.objectives_data?.objectives?.length || 0

                  return (
                    <div key={template.id} className="px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors">
                      <div className="col-span-3">
                        <p className="text-sm font-medium text-slate-900 line-clamp-1">{template.name}</p>
                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{template.description}</p>
                      </div>
                      <div className="col-span-2">
                        <Badge className={`${typeConfig.bgColor} ${typeConfig.color} text-xs`}>
                          {template.template_type.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div className="col-span-2">
                        <Badge className={`${statusConfig.bgColor} ${statusConfig.color} flex items-center gap-1 text-xs w-fit`}>
                          {statusConfig.icon}
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <div className="col-span-1 text-sm text-slate-600">
                        {objectiveCount}
                      </div>
                      <div className="col-span-1 text-sm text-slate-600">
                        {template.usage_count}
                      </div>
                      <div className="col-span-2 text-xs text-slate-600">
                        {new Date(template.created_at).toLocaleDateString()}
                      </div>
                      <div className="col-span-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          {renderTemplateDropdown(template)}
                        </DropdownMenu>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  )
}

export default TemplatesTabContent
